"use client";

import { useEffect, useRef } from "react";

// "Paper Boat" from the Randstack pattern library: a wireframe origami boat
// rocking gently as if adrift — no fill, just thin depth-shaded lines, a soft
// shadow and a dotted wake trail. Turns toward the cursor and rocks a little
// livelier on hover. Transparent canvas so the page background shows through.

interface Vec3 {
  x: number;
  y: number;
  z: number;
}

const CAM = 900;
const FOCAL = 780;

function rotY(p: Vec3, a: number): Vec3 {
  const c = Math.cos(a);
  const s = Math.sin(a);
  return { x: p.x * c + p.z * s, y: p.y, z: -p.x * s + p.z * c };
}

function rotX(p: Vec3, a: number): Vec3 {
  const c = Math.cos(a);
  const s = Math.sin(a);
  return { x: p.x, y: p.y * c - p.z * s, z: p.y * s + p.z * c };
}

function project(p: Vec3, cx: number, cy: number) {
  const scale = FOCAL / (p.z + CAM);
  return { x: cx + p.x * scale, y: cy + p.y * scale };
}

function hexToRgb(hex: string): string {
  const value = hex.replace("#", "");
  const n = parseInt(
    value.length === 3 ? value.split("").map((ch) => ch + ch).join("") : value,
    16
  );
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
}

export function PaperBoat({
  color = "#96969e",
  size = 1,
  speed = 1,
  hover = 1,
  className
}: {
  color?: string;
  size?: number;
  speed?: number;
  hover?: number;
  className?: string;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const rgb = hexToRgb(color);
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let W = 0;
    let H = 0;
    let t = 0;
    // Cursor-driven tilt offsets on top of the base pose; both decay to 0 idle.
    let curTiltX = 0;
    let curTiltY = 0;
    let speedBoost = 0;
    let rafId = 0;
    let running = false;
    let visible = false;
    const mouse = { x: -9999, y: -9999, active: false };

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = wrap!.clientWidth;
      H = wrap!.clientHeight;
      canvas!.width = W * dpr;
      canvas!.height = H * dpr;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function render() {
      ctx!.clearRect(0, 0, W, H);
      const cx = W * 0.5;
      const cy = H * 0.44;
      const SCALE = (Math.min(W, H) / 480) * size;

      // Boat geometry in local space (scaled per frame so resize just works).
      const hullLeft: Vec3 = { x: -125 * SCALE, y: 15 * SCALE, z: 8 * SCALE };
      const hullRight: Vec3 = { x: 120 * SCALE, y: 8 * SCALE, z: -10 * SCALE };
      const keel: Vec3 = { x: -5 * SCALE, y: 48 * SCALE, z: 2 * SCALE };
      const bowBase: Vec3 = { x: 95 * SCALE, y: -18 * SCALE, z: -14 * SCALE };
      const sternBase: Vec3 = { x: -95 * SCALE, y: -12 * SCALE, z: 10 * SCALE };
      const bowTip: Vec3 = { x: 155 * SCALE, y: -78 * SCALE, z: -6 * SCALE };
      const sternTip: Vec3 = { x: -148 * SCALE, y: -58 * SCALE, z: 14 * SCALE };

      const facets: Vec3[][] = [
        [hullLeft, keel, sternBase],
        [hullRight, keel, bowBase],
        [sternBase, keel, bowBase],
        [bowBase, bowTip, hullRight],
        [bowBase, bowTip, keel],
        [sternBase, sternTip, hullLeft],
        [sternBase, sternTip, keel]
      ];
      const crease: Vec3[] = [sternBase, bowBase];

      speedBoost += ((mouse.active ? 0.5 : 0) - speedBoost) * 0.04;
      if (!reduceMotion) {
        const targetYaw = mouse.active ? ((mouse.x - cx) / (W * 0.5)) * 0.35 * hover : 0;
        const targetPitch = mouse.active ? ((mouse.y - cy) / (H * 0.5)) * 0.16 * hover : 0;
        curTiltY += (targetYaw - curTiltY) * 0.02;
        curTiltX += (targetPitch - curTiltX) * 0.02;
      }

      const rock = reduceMotion ? 0 : Math.sin(t * 0.22 * speed) * 0.075 * (1 + speedBoost * 0.4);
      const bob = reduceMotion ? 0 : Math.sin(t * 0.3 * speed) * 3 * SCALE;
      const rotYA = 0.5 + rock + (reduceMotion ? 0 : curTiltY);
      const rotXA = 0.34 + (reduceMotion ? 0 : Math.sin(t * 0.17 * speed + 1.1) * 0.03 + curTiltX);

      function toScreen(pt: Vec3) {
        const r = rotX(rotY(pt, rotYA), rotXA);
        const proj = project(r, cx, cy);
        return { x: proj.x, y: proj.y + bob, z: r.z };
      }

      type Item = { type: "tri" | "line"; pts: Array<{ x: number; y: number; z: number }>; avgZ: number };
      const items: Item[] = facets.map((f) => {
        const pts = f.map(toScreen);
        return { type: "tri", pts, avgZ: (pts[0].z + pts[1].z + pts[2].z) / 3 };
      });
      {
        const pts = crease.map(toScreen);
        items.push({ type: "line", pts, avgZ: (pts[0].z + pts[1].z) / 2 });
      }
      items.sort((a, b) => a.avgZ - b.avgZ);

      let minZ = Infinity;
      let maxZ = -Infinity;
      items.forEach((it) => {
        minZ = Math.min(minZ, it.avgZ);
        maxZ = Math.max(maxZ, it.avgZ);
      });
      const zRange = Math.max(1, maxZ - minZ);

      items.forEach((it) => {
        const depthN = 1 - (it.avgZ - minZ) / zRange;
        const alpha = Math.min(0.6, 0.16 + depthN * 0.4);
        ctx!.strokeStyle = `rgba(${rgb},${alpha.toFixed(3)})`;
        ctx!.lineWidth = 0.8 + depthN * 0.5;
        ctx!.beginPath();
        ctx!.moveTo(it.pts[0].x, it.pts[0].y);
        ctx!.lineTo(it.pts[1].x, it.pts[1].y);
        if (it.type === "tri") {
          ctx!.lineTo(it.pts[2].x, it.pts[2].y);
          ctx!.closePath();
        }
        ctx!.stroke();
      });

      // Soft shadow on the "water".
      const shadowCenter = toScreen({ x: -5 * SCALE, y: 70 * SCALE, z: 0 });
      ctx!.beginPath();
      ctx!.ellipse(shadowCenter.x, shadowCenter.y + 18 * SCALE, 150 * SCALE, 14 * SCALE, 0, 0, Math.PI * 2);
      ctx!.fillStyle = `rgba(${rgb},0.10)`;
      ctx!.fill();

      // Dotted wake trailing off the stern.
      const wakeStart = toScreen({ x: -260 * SCALE, y: 64 * SCALE, z: 20 * SCALE });
      const wakeEnd = toScreen({ x: -140 * SCALE, y: 52 * SCALE, z: 14 * SCALE });
      ctx!.setLineDash([2, 7]);
      ctx!.strokeStyle = `rgba(${rgb},0.35)`;
      ctx!.lineWidth = 1;
      ctx!.beginPath();
      ctx!.moveTo(wakeStart.x, wakeStart.y);
      ctx!.quadraticCurveTo((wakeStart.x + wakeEnd.x) / 2, wakeStart.y + 10 * SCALE, wakeEnd.x, wakeEnd.y);
      ctx!.stroke();
      ctx!.setLineDash([]);
    }

    function loop() {
      t += 0.016;
      render();
      rafId = requestAnimationFrame(loop);
    }

    function start() {
      if (running) return;
      running = true;
      rafId = requestAnimationFrame(loop);
    }

    function stop() {
      if (!running) return;
      running = false;
      cancelAnimationFrame(rafId);
    }

    const onMouseMove = (e: MouseEvent) => {
      const rect = canvas!.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
      mouse.active = true;
    };
    const onMouseLeave = () => {
      mouse.active = false;
    };

    const resizeObserver = new ResizeObserver(() => {
      resize();
      render();
    });
    resizeObserver.observe(wrap);

    // Only animate while on screen; with motion reduced, one static frame.
    const intersectionObserver = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (reduceMotion) {
        if (visible) render();
      } else if (visible) {
        start();
      } else {
        stop();
      }
    });
    intersectionObserver.observe(wrap);

    resize();
    render();
    window.addEventListener("mousemove", onMouseMove);
    document.documentElement.addEventListener("mouseleave", onMouseLeave);

    return () => {
      stop();
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      window.removeEventListener("mousemove", onMouseMove);
      document.documentElement.removeEventListener("mouseleave", onMouseLeave);
    };
  }, [color, size, speed, hover]);

  return (
    <div ref={wrapRef} aria-hidden="true" className={className}>
      <canvas ref={canvasRef} className="block h-full w-full" />
    </div>
  );
}
